import Boundary, { BOUNDARY_TYPE, IBoundary } from '../abstract/Boundary';
import BoundaryEdge, { IBoundaryEdge } from '../abstract/BoundaryEdge';
import { IWall } from '../abstract/Wall';

export interface IPhysicalBoundary extends IBoundary {
  /** Legacy: still here so nothing breaks. */
  walls: Array<IWall>;
  /** New: per-wall segments with t0/t1 + side. Prefer this going forward. */
  edges: Array<IBoundaryEdge>;
}

class PhysicalBoundaryModel extends Boundary implements IPhysicalBoundary {
  walls: Array<IWall>;
  edges: Array<IBoundaryEdge>;

  constructor(walls: Array<IWall> = [], edges: Array<IBoundaryEdge> = []) {
    super(BOUNDARY_TYPE.PHYSICAL);
    this.walls = walls;
    this.edges = edges;
  }

  // ---- New API ----
  getEdges(): Array<IBoundaryEdge> {
    return this.edges;
  }

  getEdgeById(uuid: string): IBoundaryEdge | undefined {
    return this.edges.find((e) => e.uuid === uuid);
  }

  setEdges(edges: Array<IBoundaryEdge>): void {
    this.edges = edges;
  }

  addEdge(edge: IBoundaryEdge): void {
    if (this.edges.findIndex((e) => e.uuid === edge.uuid) === -1) {
      this.edges.push(edge);
    }
  }

  setEdgeById(edge: IBoundaryEdge, uuid: string): boolean {
    const idx = this.edges.findIndex((e) => e.uuid === uuid);
    if (idx === -1) return false;
    this.edges[idx] = edge;
    return true;
  }

  deleteEdgeById(uuid: string): boolean {
    const idx = this.edges.findIndex((e) => e.uuid === uuid);
    if (idx === -1) return false;
    this.edges.splice(idx, 1);
    return true;
  }

  // ---- Legacy wall passthroughs (kept for now) ----
  getWalls() {
    // Prefer edges if present; fall back to legacy array.
    if (this.edges.length > 0) {
      return this.edges.map((e) => e.wall);
    }
    return this.walls;
  }

  getWallById(uuid: string) {
    return this.getWalls().find((w) => w.uuid === uuid);
  }

  setWalls(walls: Array<IWall>) {
    this.walls = walls;
  }

  addWall(wall: IWall): void {
    this.walls.push(wall);
  }

  setWallById(wall: IWall, uuid: string): boolean {
    const relatedIndex = this.walls.findIndex((w) => w.uuid === uuid);
    if (relatedIndex === -1) return false;
    this.walls[relatedIndex] = wall;
    return true;
  }

  deleteWallById(uuid: string): boolean {
    const relatedIndex = this.walls.findIndex((w) => w.uuid === uuid);
    if (relatedIndex === -1) return false;
    this.walls.splice(relatedIndex, 1);
    return true;
  }

  toJSON() {
    return {
      ...super.toJSON(),
      // Keep tiny payloads; consumers re-link by uuid.
      walls: this.walls.map((w) => ({ uuid: w.uuid })),
      edges: this.edges.map((e) => e.toJSON?.() ?? e),
    };
  }
}

export default PhysicalBoundaryModel;
export { BoundaryEdge };
